/**
 * Display helpers for planning user assignments (inherit vs task override).
 */
import type { PlanningUserAssignment } from '../../../../api/userAssignmentApis';
import { assignRoleColor, type AssignTargetChip } from './assignUsersTypes';

export type AssignDisplaySource = 'task' | 'location';

export type AssignDisplayRow = PlanningUserAssignment & {
  source: AssignDisplaySource;
  sourceLabel: string;
};

export function resolveAssignmentsForTask(params: {
  taskId: string;
  effectiveLocationId: string | null | undefined;
  assignments: PlanningUserAssignment[];
  locationNameById?: Map<string, string> | Record<string, string>;
}): AssignDisplayRow[] {
  const taskId = String(params.taskId ?? '').trim();
  const locId = String(params.effectiveLocationId ?? '').trim();
  if (!taskId) return [];

  const locName = (() => {
    if (!locId) return locId;
    if (params.locationNameById instanceof Map) {
      return params.locationNameById.get(locId) ?? locId;
    }
    if (params.locationNameById && typeof params.locationNameById === 'object') {
      return params.locationNameById[locId] ?? locId;
    }
    return locId;
  })();

  const out: AssignDisplayRow[] = [];
  for (const row of params.assignments) {
    if (String(row.status).toLowerCase() === 'inactive') continue;
    if (row.scopeType === 'task' && row.taskId && String(row.taskId) === taskId) {
      out.push({
        ...row,
        source: 'task',
        sourceLabel: 'This task',
      });
      continue;
    }
    if (
      row.scopeType === 'location' &&
      locId &&
      row.locationId &&
      String(row.locationId) === locId
    ) {
      out.push({
        ...row,
        source: 'location',
        sourceLabel: `Inherited from ${locName}`,
      });
    }
  }

  out.sort((a, b) => {
    if (a.source !== b.source) return a.source === 'task' ? -1 : 1;
    const an = (a.userName || a.userId).toLowerCase();
    const bn = (b.userName || b.userId).toLowerCase();
    return an.localeCompare(bn);
  });
  return out;
}

/** Compact grid / summary line; overrides first. */
export function formatTaskAssigneesSummary(
  rows: AssignDisplayRow[],
  options?: { maxNames?: number },
): string {
  if (rows.length === 0) return '';
  const maxNames = options?.maxNames ?? 3;
  const labels = rows.map((r) => {
    const name = r.userName?.trim() || r.userId;
    const role = String(r.role || '').trim();
    const base = role ? `${name} (${role})` : name;
    return r.source === 'location' ? `${base} · via loc` : base;
  });
  if (labels.length <= maxNames) return labels.join(', ');
  return `${labels.slice(0, maxNames).join(', ')} +${labels.length - maxNames}`;
}

/** Unique active assignees for the Assignees column multi-select filter. */
export function listAssigneeFilterOptions(
  assignments: PlanningUserAssignment[],
): { value: string; label: string }[] {
  const byUser = new Map<string, string>();
  for (const row of assignments) {
    if (String(row.status).toLowerCase() === 'inactive') continue;
    const userId = String(row.userId ?? '').trim();
    if (!userId) continue;
    const label = row.userName?.trim() || userId;
    if (!byUser.has(userId)) byUser.set(userId, label);
  }
  return [...byUser.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

/** Initials for assignee avatars (1–2 chars). */
export function userInitials(nameOrId: string): string {
  const cleaned = String(nameOrId ?? '').trim();
  if (!cleaned) return '?';
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    const token = parts[0];
    if (token.length >= 2) return token.slice(0, 2).toUpperCase();
    return token.slice(0, 1).toUpperCase();
  }
  const first = parts[0][0] ?? '';
  const last = parts[parts.length - 1][0] ?? '';
  return `${first}${last}`.toUpperCase();
}

/**
 * HTML for Assignees column: role-colored avatar initials.
 * Caller supplies escapeHtml for attribute/text safety.
 */
export function buildAssigneeAvatarsHtml(
  rows: AssignDisplayRow[],
  escapeHtml: (s: string) => string,
  options?: { maxAvatars?: number },
): string {
  if (rows.length === 0) return '';
  const maxAvatars = options?.maxAvatars ?? 4;
  const shown = rows.slice(0, maxAvatars);
  const overflow = rows.length - shown.length;
  const parts = shown.map((r) => {
    const name = r.userName?.trim() || r.userId;
    const role = String(r.role || '').trim();
    const title =
      (role ? `${name} (${role})` : name) +
      (r.source === 'location' ? ` · ${r.sourceLabel}` : '');
    const { bg, fg } = assignRoleColor(role);
    const initials = userInitials(name);
    return (
      `<span class="gantt-assignee-avatar" style="background:${bg};color:${fg}" ` +
      `title="${escapeHtml(title)}">${escapeHtml(initials)}</span>`
    );
  });
  if (overflow > 0) {
    parts.push(
      `<span class="gantt-assignee-avatar is-more" title="${escapeHtml(
        `+${overflow} more`,
      )}">+${overflow}</span>`,
    );
  }
  return parts.join('');
}

export type TaskLikeForAssignChip = {
  id: string;
  taskName?: string | null;
  text?: string | null;
  taskType?: string | null;
  locationId?: string | null;
  parentTaskId?: string | null;
};

/** Build assign chips from selected task ids.
 * Location-type → location chip (Locations tab only).
 * Everything else (including unknown) → task chip (Tasks tab only). */
export function buildAssignTargetChipsFromTaskIds(params: {
  taskIds: Iterable<string>;
  getTask: (id: string) => TaskLikeForAssignChip | null | undefined;
  effectiveLocationIdByTaskId: Map<string, string>;
  locationOptions?: { value: string; label: string; breadcrumb?: string }[];
  taskCatalog?: { id: string; label: string; breadcrumb?: string; locationId: string | null }[];
  normalizeTaskType?: (raw: string | null | undefined) => string | null;
  resolveTaskName?: (task: TaskLikeForAssignChip) => string;
}): { chips: AssignTargetChip[]; skippedNoLocation: number } {
  const normalize =
    params.normalizeTaskType ??
    ((raw) => (raw ? String(raw).trim().toLowerCase() : null));
  const resolveName =
    params.resolveTaskName ??
    ((t) => String(t.taskName ?? t.text ?? t.id).trim() || String(t.id));
  const locOptById = new Map(
    (params.locationOptions ?? []).map((o) => [o.value, o] as const),
  );
  const catalogById = new Map((params.taskCatalog ?? []).map((t) => [t.id, t] as const));

  const chips: AssignTargetChip[] = [];
  const locationChipByLoc = new Map<string, AssignTargetChip>();
  const seenTasks = new Set<string>();
  let skippedNoLocation = 0;

  for (const rawId of params.taskIds) {
    const id = String(rawId).trim();
    if (!id) continue;
    const task = params.getTask(id);
    if (!task) continue;
    const loc =
      String(task.locationId ?? '').trim() ||
      String(params.effectiveLocationIdByTaskId.get(id) ?? '').trim();
    if (!loc) {
      skippedNoLocation += 1;
      continue;
    }

    const type = normalize(task.taskType) ?? 'unknown';
    // Only true location-type rows become location chips.
    // Unknown / phase / activity / etc. always stay under Tasks.
    if (type === 'location') {
      const existing = locationChipByLoc.get(loc);
      if (existing) {
        if (!existing.sourceTaskIds?.includes(id)) {
          existing.sourceTaskIds = [...(existing.sourceTaskIds ?? []), id];
        }
        continue;
      }
      const locOpt = locOptById.get(loc);
      locationChipByLoc.set(loc, {
        kind: 'location',
        id: loc,
        label: locOpt?.label || loc,
        locationId: loc,
        breadcrumb: locOpt?.breadcrumb,
        sourceTaskIds: [id],
      });
      continue;
    }

    if (seenTasks.has(id)) continue;
    seenTasks.add(id);
    const catalog = catalogById.get(id);
    chips.push({
      kind: 'task',
      id,
      label: catalog?.label || resolveName(task) || id,
      locationId: loc,
      breadcrumb: catalog?.breadcrumb,
      sourceTaskIds: [id],
    });
  }

  return {
    chips: [...locationChipByLoc.values(), ...chips],
    skippedNoLocation,
  };
}
